import pluralize from 'pluralize';

import {
  Column,
  DataTable,
  LayoutCard,
  NoData,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import type { BillingV1FeatureDailyUsage } from '@cast/types';

import { tableCacheKeys } from 'constants/tableCacheKeys';

import { FailedToLoad } from '@cast/shared-messages-ui';

type DailyUsageTableProps = {
  dailyUsages: BillingV1FeatureDailyUsage[];
  unit: string;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

export const DailyUsageTable = ({
  dailyUsages,
  unit,
  isLoading,
  isError,
  refetch,
}: DailyUsageTableProps) => {
  const sorted = [...dailyUsages].sort((a, b) =>
    (b.day ?? '').localeCompare(a.day ?? '')
  );
  const dayCount = sorted.length;

  return (
    <LayoutCard
      title={
        <Typography
          variant="A1"
          color={tokens.theme.fg.structural.primary}
          data-testid="daily-records-count"
        >
          {isLoading ? 'Days' : `${dayCount} ${pluralize('day', dayCount)}`}
        </Typography>
      }
      testId="platform-usage-daily-card"
      padding={false}
      slotProps={{
        header: { py: 12 },
        content: {
          gap: 12,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DataTable<BillingV1FeatureDailyUsage>
        cacheKey={tableCacheKeys.PLATFORM_USAGE.DAILY}
        testId="platform-usage-daily-table"
        rowKey={(item) => item.day ?? ''}
        data={sorted}
        isLoading={isLoading}
        error={isError}
        components={{
          noData: <NoData entity="daily usage" />,
          failed: <FailedToLoad refresh={refetch} />,
        }}
      >
        <Column<BillingV1FeatureDailyUsage>
          id="day"
          header="Date"
          minWidth={200}
          sortable
          accessor="day"
          renderCell={(item) => (
            <Typography
              color={tokens.theme.fg.structural.primary}
              variant="P12R"
            >
              {item.day ?? '–'}
            </Typography>
          )}
        />
        <Column<BillingV1FeatureDailyUsage>
          id="usage"
          header="Usage"
          infoTooltip={`Total ${unit} usage for this day`}
          width={200}
          sortable
          accessor="value"
          renderCell={(item) => (
            <Stack direction="row" alignItems="baseline" gap={4}>
              <Typography
                color={tokens.theme.fg.structural.primary}
                variant="P12B"
              >
                {(item.value ?? 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
              >
                {unit}
              </Typography>
            </Stack>
          )}
        />
      </DataTable>
    </LayoutCard>
  );
};
